<script lang="ts" setup>
  import type { Partner } from '@api/models';
  import { computed, ref, useTemplateRef } from 'vue';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import type { ButtonVariants } from '@ui/components/button';
  import { ComboSelect } from '@ui/components/combo-select';

  import { toPartnerRef, usePartnerSelect } from '@/composables/usePartnerSelect';
  import type { PartnerRef } from '@/composables/usePartnerSelect';
  import { usePartners } from '@/store';

  interface Props {
    clearable?: boolean;
    disabled?: boolean;
    initialOpen?: boolean;
    partnerId?: string;
    showAccountNumber?: boolean;
    triggerClass?: HTMLAttributes['class'];
    triggerSize?: ButtonVariants['size'];
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'update', partner?: Partner): void;
  }>();

  const getComputedName = (p: PartnerRef | undefined | null): string => {
    if (!p) return '';
    return p.name ?? p.accountNumber ?? p.id ?? '';
  };

  const partnerStore = usePartners();

  const { selectedPartner, isLoading, partnerLoader } = usePartnerSelect({
    partnerId: () => props.partnerId,
  });

  /** Normalized view of selectedPartner — use this in the template for display. */
  const selectedRef = computed<PartnerRef | null>(() =>
    selectedPartner.value ? toPartnerRef(selectedPartner.value) : null,
  );

  const isOpen = ref(props.initialOpen ?? false);

  /** True while the async peekFindById fetch triggered by a user selection is in-flight. */
  const isUpdating = ref(false);

  const SelectRef = useTemplateRef('SelectRef');
  const TriggerRef = useTemplateRef('TriggerRef');

  defineExpose({
    selectEl: SelectRef,
    triggerEl: TriggerRef,
    isUpdating,
  });
</script>

<template>
  <ComboSelect
    ref="SelectRef"
    v-model="selectedPartner"
    v-model:open="isOpen"
    :loader="partnerLoader"
    searchable
    popover-side="bottom"
    placeholder="Select Partner"
    search-placeholder="Select partner..."
    option-identifier="id"
    variant="dropdown"
    :clearable="clearable"
    :disabled="disabled || isLoading"
    dropdown-class="max-w-80"
    @update:model-value="
      async (value) => {
        if (value && !Array.isArray(value)) {
          // TODO: migrate to emitting PartnerRef once CallAgentDebugConfigurationPanel no longer
          // needs primaryPhoneContact from the full Partner model. Until then, this legacy fetch
          // is required to satisfy that consumer.
          isUpdating = true;
          try {
            const partner = await partnerStore.peekFindById((value as { id: string }).id);
            emit('update', partner as Partner);
          } finally {
            isUpdating = false;
          }
        }
      }
    "
  >
    <template #trigger>
      <Button
        ref="TriggerRef"
        class="w-full justify-between hover:bg-background"
        :class="triggerClass"
        :disabled="disabled || isLoading || partnerLoader.loading.value"
        variant="outline"
        :size="triggerSize"
      >
        <div v-if="getComputedName(selectedRef)" class="flex items-center gap-2 min-w-0 flex-1">
          <span class="truncate">{{ getComputedName(selectedRef) }}</span>
          <span
            v-if="showAccountNumber && selectedRef?.accountNumber"
            class="text-muted-foreground font-normal shrink-0"
          >
            {{ selectedRef.accountNumber }}
          </span>
        </div>

        <template v-else> Select a Customer </template>
        <span class="ml-auto flex items-center gap-1.5 shrink-0">
          <slot name="trigger-adornment" />
          <i class="fal fa-chevron-down text-3xs!"></i>
        </span>
      </Button>
    </template>

    <template #fixedBottom="{ closeSelect }">
      <div class="flex flex-col gap-y-1">
        <Button
          v-if="clearable"
          class="text-left"
          variant="ghost"
          size="sm"
          @click="
            selectedPartner = null;
            emit('update', undefined);
            closeSelect();
          "
        >
          Clear selection
        </Button>
      </div>
    </template>

    <template #selected="{}">
      {{ getComputedName(selectedRef) }}
    </template>

    <template #option="{ option }">
      <span class="flex items-center justify-between w-full gap-2">
        <span class="truncate">{{ toPartnerRef(option).name }}</span>
        <span
          v-if="showAccountNumber && toPartnerRef(option).accountNumber"
          class="text-muted-foreground shrink-0"
        >
          {{ toPartnerRef(option).accountNumber }}
        </span>
      </span>
    </template>
  </ComboSelect>
</template>
